import React, {useCallback, useEffect, useMemo, useState} from "react";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import {DatePickerForm} from "../../base/date-picker";
import Button from "../../base/button/button";
import {DropdownForm} from "../../base/dropdown/dropdown";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";


const formValues = {
    operator: 'operator',
    startDate: 'startDate',
    endDate: 'endDate',
}

const schema = Yup.object().shape({
    [formValues.operator]: Yup.object().nullable(),
    [formValues.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formValues.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const BulkPriceChangeSearchSection = ({searchData}) => {
    const [operators, setOperators] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Fetches the operators from server:
     * - Calls the api regarding this action
     * - in case of success, sets the [operators] in inner state with received values
     */
    const getOperators = useCallback(async () => {
        const forApi = {
            filters: {},
            paginationInfo: {
                currentPage: 1,
                pageSize: 100,
            },
        }
        const response = await Api.searchOperatorsLocal(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOperators(response?.data?.resultList ?? []);
        }
    }, [isMounted])

    /**
     * With initial render of the component,
     * Calls the function for getting the list of operators
     */
    useEffect(() => {
        getOperators().then();
    }, [getOperators])


    /**
     * Memo version of the initial values in the form
     * @type {any}
     */
    const initialValues = useMemo(() => ({
        [formValues.operator]: operators?.find(e => e.id?.toString() === searchData.filters?.operators),
        [formValues.startDate]: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters?.startDate) : undefined,
        [formValues.endDate]: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters?.endDate) : undefined,
    }), [searchData.filters, operators]);

    /**
     * Changes the query based on the added filters which triggers the search in responsible view
     * @param values
     */
    const onSubmit = (values) => {
        searchData.setFilters({
            operators: !!values[formValues.operator] ? values[formValues.operator].id : undefined,
            startDate: values[formValues.startDate] ? DateUtils.UTCToServer(values[formValues.startDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values[formValues.endDate] ? DateUtils.UTCToServer(values[formValues.endDate]).format(DateUtils.withoutOffsetFormat) : undefined,
        });
    }

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values}) => {
                return (
                    <Row className='d-flex align-items-center justify-content-center'>
                        <Col xs={12} md={6} lg={12} className={'mb-3'}>
                            <DropdownForm
                                padding={'3px 15px'}
                                label={'Operator'}
                                className={'py-0'}
                                name={formValues.operator}
                                required={false}
                                renderSelectedValue={(e) => !e ? "" : e.firstName + " " + e.lastName}
                                data={operators.map(e => ({
                                    value: e,
                                    label: e.firstName + " " + e.lastName,
                                    id: e.id,
                                })) ?? []}
                            />
                        </Col>
                        <Col xs={12} md={6} lg={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.startDate}
                                label={'Start Date'}
                                required={required[formValues.startDate]}
                                maxDate={values[formValues.endDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} md={6} lg={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.endDate}
                                label={'End Date'}
                                required={required[formValues.endDate]}
                                minDate={values[formValues.startDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12}
                             className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={!!submitting || searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                {
                                    submitting
                                        ? "Searching..."
                                        : "Search"
                                }
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default BulkPriceChangeSearchSection;
